"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AudioRecorderStatus, RecordedAudioPayload } from "../types";
import {
  AUDIO_RECORDER_MAX_DURATION_MS,
  buildAudioRecordingFileName,
  getSupportedAudioRecorderMimeType,
  isAudioRecordingSupported,
} from "../utils/mediaRecorder";

function revokeRecording(recording: RecordedAudioPayload | null) {
  if (recording?.objectUrl) {
    URL.revokeObjectURL(recording.objectUrl);
  }
}

export default function useVoiceRecorder() {
  const [status, setStatus] = useState<AudioRecorderStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [recording, setRecording] = useState<RecordedAudioPayload | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const startedAtRef = useRef<number | null>(null);
  const durationIntervalRef = useRef<number | null>(null);
  const maxDurationTimeoutRef = useRef<number | null>(null);
  const discardNextRecordingRef = useRef(false);
  const autoStoppedRef = useRef(false);

  const isSupported = useMemo(() => isAudioRecordingSupported(), []);
  const supportedMime = useMemo(() => getSupportedAudioRecorderMimeType(), []);
  const maxDurationSeconds = AUDIO_RECORDER_MAX_DURATION_MS / 1000;

  const clearTimers = useCallback(() => {
    if (durationIntervalRef.current !== null) {
      window.clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    if (maxDurationTimeoutRef.current !== null) {
      window.clearTimeout(maxDurationTimeoutRef.current);
      maxDurationTimeoutRef.current = null;
    }
  }, []);

  const stopMediaStream = useCallback(() => {
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
  }, []);

  const finalizeCleanup = useCallback(() => {
    clearTimers();
    stopMediaStream();
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    startedAtRef.current = null;
    setElapsedMs(0);
  }, [clearTimers, stopMediaStream]);

  const resetRecording = useCallback(() => {
    discardNextRecordingRef.current = true;
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    } else {
      finalizeCleanup();
    }

    setError(null);
    setNotice(null);
    setStatus("idle");
    setRecording((current) => {
      revokeRecording(current);
      return null;
    });
  }, [finalizeCleanup]);

  const stopRecording = useCallback(() => {
    if (
      !mediaRecorderRef.current ||
      mediaRecorderRef.current.state === "inactive"
    ) {
      return;
    }

    mediaRecorderRef.current.stop();
  }, []);

  const startRecording = useCallback(async () => {
    if (!isSupported) {
      setStatus("error");
      setError("Voice recording is not supported on this device or browser.");
      return;
    }

    if (!supportedMime) {
      setStatus("error");
      setError("No supported audio recording format was detected.");
      return;
    }

    try {
      revokeRecording(recording);
      setRecording(null);
      setError(null);
      setNotice(null);
      setElapsedMs(0);
      discardNextRecordingRef.current = false;
      autoStoppedRef.current = false;
      chunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const mediaRecorder = supportedMime.mimeType
        ? new MediaRecorder(stream, { mimeType: supportedMime.mimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = mediaRecorder;
      startedAtRef.current = Date.now();

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onerror = () => {
        setStatus("error");
        setError("Recording failed. Please try again.");
        finalizeCleanup();
      };

      mediaRecorder.onstop = () => {
        const startedAt = startedAtRef.current ?? Date.now();
        const durationSeconds = Math.max(
          1,
          Math.round(((Date.now() - startedAt) / 1000) * 10) / 10,
        );

        if (discardNextRecordingRef.current) {
          discardNextRecordingRef.current = false;
          finalizeCleanup();
          return;
        }

        const blobType =
          mediaRecorder.mimeType || supportedMime.mimeType || "audio/ogg";
        const blob = new Blob(chunksRef.current, { type: blobType });
        const fileName = buildAudioRecordingFileName(supportedMime.fileExtension);
        const file = new File([blob], fileName, { type: blobType });
        const objectUrl = URL.createObjectURL(blob);

        setRecording((current) => {
          revokeRecording(current);
          return {
            blob,
            file,
            fileName,
            mimeType: blobType,
            fileExtension: supportedMime.fileExtension,
            durationSeconds,
            objectUrl,
          };
        });

        if (autoStoppedRef.current) {
          setNotice(
            `Recording reached the ${maxDurationSeconds}-second limit and stopped automatically.`,
          );
          autoStoppedRef.current = false;
        }

        setStatus("recorded");
        finalizeCleanup();
      };

      mediaRecorder.start();
      setStatus("recording");

      durationIntervalRef.current = window.setInterval(() => {
        const started = startedAtRef.current;
        if (started) {
          setElapsedMs(Date.now() - started);
        }
      }, 250);

      maxDurationTimeoutRef.current = window.setTimeout(() => {
        autoStoppedRef.current = true;
        stopRecording();
      }, AUDIO_RECORDER_MAX_DURATION_MS);
    } catch (recordingError) {
      setStatus("error");
      setError(
        recordingError instanceof Error
          ? recordingError.message
          : "Unable to access the microphone.",
      );
      finalizeCleanup();
    }
  }, [
    finalizeCleanup,
    isSupported,
    maxDurationSeconds,
    recording,
    stopRecording,
    supportedMime,
  ]);

  useEffect(() => {
    return () => {
      revokeRecording(recording);
      finalizeCleanup();
    };
  }, [finalizeCleanup, recording]);

  return {
    error,
    notice,
    elapsedMs,
    isSupported,
    maxDurationSeconds,
    recording,
    status,
    startRecording,
    stopRecording,
    resetRecording,
  };
}
